import { TransactionStatus, TransactionUnitPriceSource } from '../constants/transaction';
import { IMatter } from './matter';
import { IMatterPhase } from './matterPhase';
import { IPrincipalCompany } from './principalCompany';
import { IPrincipalPrivatePerson } from './principalPrivatePerson';
import { ITransactionTypes } from './transactionTypes';
import { IUnit } from './unit';

export interface ITransaction {
  createdOn: string;
  modifiedOn: string;
  createdById: string;
  modifiedById: string;
  createdBySource: string;
  modifiedBySource: string;
  version: string;
  revision: number;
  id?: string;
  subject: string;
  currentSubject?: string;
  transactionDate: string;
  totalPrice: number;
  totalPriceInCurrency: number;
  originalTotalPrice: string;
  originalTotalPriceInCurrency: number;
  totalPriceSetManually: boolean;
  isTotalPriceChangedManually?: boolean;
  exchangeRate: string;
  status: TransactionStatus;
  billableQuantity: number;
  isManualBillableQuantity?: boolean;
  workedHours: number;
  isManualWorkedHours?: boolean;
  unitPrice: number;
  isManualUnitPrice?: boolean;
  isUnitPriceFromTransactionType?: boolean;
  shouldLoadServerPrice?: boolean;
  unitPriceInCurrency: number;
  restrictPriceUpdate?: boolean;
  taxFree: boolean;
  description: string;
  ignoreDiscount: boolean;
  reviewedDate: string;
  systemReviewed: boolean;
  isSubscriptionBased: boolean;
  priceSource: string;
  principalCompanyId: string;
  principalCompany: IPrincipalCompany;
  principalPrivatePersonId: string;
  principalPrivatePerson: IPrincipalPrivatePerson;
  onlyHourBasedTransactionTypes?: boolean;
  matterId: string;
  overrideWarnings?: boolean;
  transactionUserId: string;
  transactionUser: ITransactionUser;
  transactionTypeId: string;
  transactionType: ITransactionTypes;
  skillId: string;
  salesAccountId: string;
  costCenterId: string;
  vatRateId: string;
  unitId: string;
  reviewerId: string;
  preliminaryInvoiceId: string;
  matterPhaseId: string;
  hasImportantNotes: boolean;
  hasNotes: boolean;
  matter: IMatter;
  unit: IUnit;
  skill: ISkill;
  isSubjectSetByUser?: boolean;
  isManualSkill?: boolean; // the transaction is stored in context (it persists across navigation), so when you clear the skill, transaction.skill becomes empty and the effect re-fires on return, re-applying the suggestion. I need a flag that records the suggestion was already applied (or the user touched the field), so it isn't re-applied.
  isFromMailOrAppointment?: boolean;
  disableMatterChange?: boolean;
  activityId?: string;
  workTimeId?: string;
  isCopiedTransaction?: boolean;
  isCopyBannerDismissed?: boolean;
  matterPhase: IMatterPhase;
  timerId?: string | null;
  invoiceableType: string;
  mailAsNote: boolean;
  mailAsNoteWithoutAttachments?: boolean;
  mailAsNotewithoutAttachmentsWarning?: boolean;
  isDraft?: boolean;
  shouldFetchDefaultTransactionType?: boolean;
  roundingRuleApplied?: string;
}
export interface IHourTransaction {
  transactionDate: string;
  billableHours: number;
  formatedDate?: string;
}

export interface IParameters {
  parameters: { transactionParameters: ITransactionParameters };
}

export interface ITransactionParameters {
  skillRequired: boolean;
  transactionTypeInUse: boolean;
  transactionTypeRequired: boolean;
  invoiceableDurationStep: string;
  minimumInvoiceableDurationInHours: number;
  defaultTransactionTypeId: string;
  currency: string;
}
export interface ITranslation {
  language: string;
  translation: string;
}
export interface IWeeklyTransaction {
  billableHours: number;
  budgetedHours: number;
  invoicedHours: number;
  weekNumber: number;
  year: number;
  formatedWeek: string;
}

export interface ITransactionFilter {
  label: string;
  value: string;
}
export interface ICreateTransaction {
  createdBySource: string;
  subject: string;
  transactionDate: string;
  billableQuantity: number;
  workedHours: number;
  taxFree?: boolean;
  description?: string;
  matterId: string;
  transactionUserId: string;
  unitId?: string;
  ignoreDiscount?: boolean;
  unitPrice?: number;
  totalPrice?: number;
  transactionTypeId?: string;
  skillId?: string;
  unitPriceInCurrency?: number;
  totalPriceInCurrency?: number;
  matterPhaseId?: string | null;
  priceSource?: TransactionUnitPriceSource;
}
export interface IUpdateTransaction {
  subject: string;
  transactionDate: string;
  billableQuantity: number;
  workedHours: number;
  taxFree?: boolean;
  description?: string;
  transactionUserId: string;
  unitId?: string;
  ignoreDiscount?: boolean;
  unitPrice?: number;
  totalPrice?: number;
  transactionTypeId?: string;
  version: string;
  modifiedBySource: string;
  skillId?: string;
  unitPriceInCurrency?: number;
  totalPriceInCurrency?: number;
  matterPhaseId?: string | null;
  priceSource?: TransactionUnitPriceSource;
}
export interface ISkill {
  id: string;
  name: string;
}
export interface ITransactionUser {
  id: string;
  fullName: string;
}
export interface IFilterOption {
  label: string;
  value: string;
}
export interface IServerUnitPrice {
  units: number;
  type: string;
  ignoreDiscount: boolean;
  unitPrice: number;
  source: string;
}
export interface IRegionalNumberFormat {
  value: number;
  minimumFractionDigits?: number;
}
export interface ILast7DaysHourTransactionDashboard {
  transactionDate: string;
  billableHours: number;
}
export interface IDailyGroupedTransactions {
  transactions: ITransaction[];
  date: string;
  amount: number;
}
export interface ITransactionUnitPrice {
  unitPrice: number;
  units: number;
  source: TransactionUnitPriceSource;
  type: TransactionUnitPriceType;
  currency?: string;
  ignoreDiscount?: boolean;
  standardPrice?: number;
  otherInfo?: string;
}

export enum TransactionUnitPriceType {
  MATTER = 'matter',
  HOUR_BASED = 'hour-based',
  NON_HOUR_BASED = 'non-hour-based',
  UNKNOWN = 'unknown',
}
